import type { ChoiceLabel } from "./types";

// Anonymous practice attempts, held in memory for the lifetime of the
// app load. A logged-out user grades client-side (publicSample ships the
// correctLabel), so these never hit the server until the user signs up —
// at which point email-gate threads them into the magic-link callback as
// `?claim=`, and index.tsx persists them via questions.claimAnonymous.
//
// The encode shape MUST match index.tsx#tryDecodeClaim: a base64 JSON
// array of { questionId, answer, msSpent }, capped at 10 entries.

export interface AnonAttempt {
  questionId: string;
  answer: ChoiceLabel;
  msSpent: number;
}

const MAX_ATTEMPTS = 10;

let attempts: AnonAttempt[] = [];

export function recordAnonAttempt(attempt: AnonAttempt): void {
  attempts.push(attempt);
  // Keep the most recent — the claim endpoint caps at 10 and a single
  // free batch is 5, so this only matters if a user practices several
  // batches before gating.
  if (attempts.length > MAX_ATTEMPTS) {
    attempts = attempts.slice(-MAX_ATTEMPTS);
  }
}

export function clearAnonAttempts(): void {
  attempts = [];
}

export function hasAnonAttempts(): boolean {
  return attempts.length > 0;
}

function toBase64(raw: string): string {
  // `btoa` is a guaranteed global on web and on Hermes (Expo polyfills it),
  // so there is no reachable native fallback to write. `Buffer` is NOT a
  // Hermes global, so the old `Buffer.from(...)` branch would have thrown a
  // ReferenceError had it ever run. The payload is ASCII JSON, so byte-wise
  // btoa is safe here.
  if (typeof btoa !== "function") {
    throw new Error("base64 encoder (btoa) unavailable in this runtime");
  }
  return btoa(raw);
}

// URL-safe base64 alphabet: '+'→'-', '/'→'_', strip '=' padding. Must
// match the SEO encoder (apps/seo/src/scripts/quiz.ts) and the decoder in
// index.tsx#tryDecodeClaim, which reverses this before atob. Standard
// base64's '+'/'/' can be mangled when carried as a URL query param.
function toUrlSafeBase64(b64: string): string {
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// URL-safe base64-encoded claim payload for the magic-link callbackURL, or
// null when there's nothing to claim. Caller is responsible for URL-encoding.
export function encodeAnonClaim(): string | null {
  if (attempts.length === 0) return null;
  const payload = attempts.slice(-MAX_ATTEMPTS).map((a) => ({
    questionId: a.questionId,
    answer: a.answer,
    msSpent: Math.floor(Math.max(0, a.msSpent)),
  }));
  return toUrlSafeBase64(toBase64(JSON.stringify(payload)));
}
